import type { GetStaticProps, NextPage } from 'next'
import type { TemplateCard, TemplateCategory, TemplateTag } from '@/lib/cms'

import { Seo } from '@/components/generals/seo'
import { JsonLd } from '@/components/generals/json-ld'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/animation'
import { TemplatesSection } from '@/components/templates/templates-section'
import { useTranslation } from '@/i18n'
import { useSharedTranslation } from '@/i18n/shared'
import { getAllTemplates, getTemplateCategories, getTemplateTags } from '@/lib/cms'
import { homeSchema } from '@/lib/schema'

interface HomeProps {
    templates: TemplateCard[]
    categories: TemplateCategory[]
    tags: TemplateTag[]
    topCategories: TemplateCategory[]
    topTags: TemplateTag[]
}

const Home: NextPage<HomeProps> = ({ templates, categories, tags, topCategories, topTags }) => {
    const { t } = useTranslation()
    const s = useSharedTranslation()

    return (
        <>
            <Seo title={s.seo.homeTitle} />
            <JsonLd schema={homeSchema(s.seo.homeSchemaName, s.seo.siteDescription)} />

            <Layout topCategories={topCategories} topTags={topTags}>
                <div className='section'>
                    <div className='container relative'>
                        <div className='small-hero-content'>
                            <Reveal trigger='load' delay={0}>
                                <div className='new-badge-wrap'>
                                    <div className='new-badge'>
                                        <div>{t.home.badgeOff}</div>
                                    </div>
                                    <div> {t.home.badgePages}</div>
                                </div>
                            </Reveal>
                            <Reveal trigger='load' delay={0.1}>
                                <h1 className='hero-text-02 home-hero-text no-margin-top'>{t.home.heroTitle}</h1>
                            </Reveal>
                            <Reveal trigger='load' delay={0.2}>
                                <p className='grey-paragraph'>{t.home.heroSubtitle}</p>
                            </Reveal>
                            <Reveal trigger='load' delay={0.3}>
                                <div className='margin-small margin-top'>
                                    <a href='https://app.wedoflow.com' className='button button-white-rounded w-button'>
                                        {t.home.heroCta}
                                    </a>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>

                <div className='section hide'>
                    <div className='container'>
                        <div className='all-access-banner'>
                            <div className='new-banner-content'>
                                <h2 className='no-margin-top'>{t.home.allAccessTitle}</h2>
                                <p className='grey-paragraph'>
                                    {t.home.allAccessLead} <span className='white-text'>{t.home.allAccessLeadStrong}</span> {t.home.allAccessMid}{' '}
                                    <span className='white-text'>{t.home.allAccessStrong2}</span> {t.home.allAccessTail}
                                </p>
                                <div className='margin-medium margin-top'>
                                    <a href='https://app.wedoflow.com' className='button button-white-rounded w-button'>
                                        {t.home.allAccessCta}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <img src='https://wedoflow.cdn.modulify.website/images/New-Banner.png' loading='lazy' alt='' className='new-banner-image' />
                            </div>
                        </div>
                    </div>
                </div>

                <div id='WebflowTemplates' className='section'>
                    <div className='container _4ems'>
                        <Reveal trigger='load' delay={0.2}>
                            <TemplatesSection templates={templates} categories={categories} tags={tags} />
                        </Reveal>
                    </div>
                </div>
            </Layout>
        </>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async ({ locale }) => {
    const [templates, categories, tags] = await Promise.all([
        getAllTemplates(),
        getTemplateCategories(locale),
        getTemplateTags(locale)
    ])

    return {
        props: {
            templates,
            categories,
            tags,
            topCategories: categories.slice(0, 6),
            topTags: tags.slice(0, 8)
        },
        revalidate: 3600
    }
}

export default Home
